import React from 'react';
import {
  Box,
  Button, Card, Grid, Typography,
} from '@mui/material';
import { Category } from '@gamiphy/bl/mission';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import { UserStore } from '@gamiphy/loyalty-station-desktop/store/user';
import { DisplayStringTrans, useDisplayStringValue, useTranslation } from '@gamiphy/react-i18n';
import { UnavowedPopup } from '@gamiphy/loyalty-station-desktop/components/popups/unavowed';
import { observer } from 'mobx-react';
import SVG from 'react-inlinesvg';
import { MissionsStore } from '@gamiphy/loyalty-station-desktop/store/mission';

export type Props = {
  category: Category,
  open: boolean,
  setCategoryPopupOpen: (arg: boolean) => void,
  setSelectedParallelCategory: (arg: Category) => void,
}

const CategoryContent: React.FC<Props> = observer(({
  category,
  setCategoryPopupOpen,
  setSelectedParallelCategory,
}) => {
  const { t } = useTranslation();
  const [unavowedPopupOpen, setUnavowedPopupOpen] = React.useState(false);
  const missionDescription = useDisplayStringValue(category.title);

  const onCategoryClick = React.useCallback(() => {
    if (UserStore.isRealUser) {
      setCategoryPopupOpen(true);
      setSelectedParallelCategory(category);
    } else {
      setUnavowedPopupOpen(true);
    }
  }, [UserStore.isRealUser]);

  // Mark category as completed when all missions in it are completed
  const parallelCategoryCompleted = React.useMemo(() => (
    MissionsStore.isCategoryCompleted(category)
  ), [MissionsStore.userMissions, category]);

  return (
    <Card sx={{
      m: 0,
      width: '100%',
      boxShadow: 'none',
      borderRadius: 0,
      pb: 2,
    }}
    >
      <Grid
        container
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        wrap="nowrap"
        columnSpacing={1}
      >
        <Grid
          item
          sx={{
            '& path': {
              stroke: (theme) => theme.palette.primary.main,
            },
          }}
        >
          <SVG
            src={category.image}
          >
            <Box
              component="img"
              src={category.image}
              sx={{
                width: '2.4rem',
                height: '2.4rem',
              }}
              alt=""
            />
          </SVG>
        </Grid>
        <Grid item sx={{ flex: 2 }} container direction="column">
          <Grid item xs={12}>
            <Typography
              variant="subtitle1"
              sx={{
                fontSize: '1.2rem',
                fontWeight: 500,
                lineHeight: 1.25,
                mb: 0.4,
              }}
              title={missionDescription}
            >
              <DisplayStringTrans
                displayString={category.title}
              />
            </Typography>
          </Grid>
        </Grid>
        {parallelCategoryCompleted ? (
          <CheckCircleOutlineOutlinedIcon color="success" fontSize="large" />
        ) : (
          <Grid item>
            <Button
              sx={{
                width: '8rem',
                fontSize: '1.2rem',
              }}
              fullWidth
              variant="contained"
              color="primary"
              onClick={onCategoryClick}
            >
              {t('missions.complete', 'Complete Missions')}
            </Button>
            <UnavowedPopup
              open={unavowedPopupOpen}
              onClose={() => setUnavowedPopupOpen(false)}
              title={t('unavowedPopup.rewardTitle', 'Sign up now to redeem your coupon for exclusive discounts!')}
            />
          </Grid>
        )}

      </Grid>
    </Card>
  );
});

export default CategoryContent;
